// web/src/pages/finance/TransactionsPage.tsx
//
// I-6 — Transactions browser.
//
// Filter-driven list with pagination. Mirrors InvoicesListPage pagination shape.
// Drill-through: invoice row → /invoices/{id}; payment row → /payments?status=Posted&paymentRecordId={id}.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  listTransactions,
  type TransactionRowDto,
  type ListTransactionsParams,
} from "@/lib/invoices-payments";
import { AllmarkDatePicker } from "@/components/allmark-date-picker";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

interface FilterState {
  accountId: string;
  typeCode: string;
  from: string | null;
  to: string | null;
  invoiceId: string;
  paymentRecordId: string;
  minAmount: string;
  maxAmount: string;
}

const EMPTY_FILTERS: FilterState = {
  accountId: "",
  typeCode: "",
  from: null,
  to: null,
  invoiceId: "",
  paymentRecordId: "",
  minAmount: "",
  maxAmount: "",
};

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, page: number): ListTransactionsParams {
  return {
    accountId: state.accountId ? Number(state.accountId) : undefined,
    typeCode: state.typeCode || undefined,
    from: state.from ?? undefined,
    to: state.to ?? undefined,
    invoiceId: state.invoiceId ? Number(state.invoiceId) : undefined,
    paymentRecordId: state.paymentRecordId ? Number(state.paymentRecordId) : undefined,
    minAmount: state.minAmount ? Number(state.minAmount) : undefined,
    maxAmount: state.maxAmount ? Number(state.maxAmount) : undefined,
    page,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Source column helper — Invoice ref / Payment ref / em-dash
// ---------------------------------------------------------------------------

function sourceLabel(row: TransactionRowDto): string {
  if (row.invoiceReferenceNo) return `Inv ${row.invoiceReferenceNo}`;
  if (row.paymentReferenceNo) return `Pmt ${row.paymentReferenceNo}`;
  return "—";
}

// ---------------------------------------------------------------------------
// TransactionsPage
// ---------------------------------------------------------------------------

export function TransactionsPage() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  const params = useMemo(() => buildParams(filters, page), [filters, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["transactions", params],
    queryFn: () => listTransactions(params),
  });

  // -------------------------------------------------------------------------
  // Filter helpers — each change resets page to 0
  // -------------------------------------------------------------------------

  function setFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }

  // -------------------------------------------------------------------------
  // Row click — drill through to invoice or payment
  // -------------------------------------------------------------------------

  function handleRowClick(row: TransactionRowDto) {
    if (row.invoiceId != null) {
      navigate(`/invoices/${row.invoiceId}`);
    } else if (row.paymentRecordId != null) {
      navigate(`/payments?status=Posted&paymentRecordId=${row.paymentRecordId}`);
    }
    // else: account-credit transaction with no invoice or payment — no-op
  }

  // -------------------------------------------------------------------------
  // Columns
  // -------------------------------------------------------------------------

  const cols: DataGridColumn<TransactionRowDto>[] = [
    {
      id: "date",
      header: "Date",
      accessor: (r) =>
        r.transactionDate
          ? new Date(r.transactionDate).toLocaleDateString("en-AU")
          : "—",
    },
    {
      id: "account",
      header: "Account",
      accessor: (r) => r.accountReferenceNo ?? "—",
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) => r.tranTypeCode,
    },
    {
      id: "source",
      header: "Source",
      accessor: (r) => sourceLabel(r),
    },
    {
      id: "amount",
      header: "Amount",
      accessor: (r) => fmt.format(r.tranAmount),
      align: "right",
    },
    {
      id: "createdBy",
      header: "Created by",
      accessor: (r) => r.createdByName ?? "—",
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <ListPage
      title="Transactions"
      filterBar={
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Account ID */}
            <div>
              <Label htmlFor="txn-account">Account ID</Label>
              <Input
                id="txn-account"
                type="number"
                placeholder="Account ID"
                value={filters.accountId}
                onChange={(e) => setFilter("accountId", e.target.value)}
              />
            </div>

            {/* Type code */}
            <div>
              <Label htmlFor="txn-type">Type code</Label>
              <Input
                id="txn-type"
                placeholder="e.g. CREDIT"
                value={filters.typeCode}
                onChange={(e) => setFilter("typeCode", e.target.value)}
              />
            </div>

            {/* Source invoice */}
            <div>
              <Label htmlFor="txn-invoice">Source invoice ID</Label>
              <Input
                id="txn-invoice"
                type="number"
                placeholder="Invoice ID"
                value={filters.invoiceId}
                onChange={(e) => setFilter("invoiceId", e.target.value)}
              />
            </div>

            {/* Source payment */}
            <div>
              <Label htmlFor="txn-payment">Source payment ID</Label>
              <Input
                id="txn-payment"
                type="number"
                placeholder="Payment ID"
                value={filters.paymentRecordId}
                onChange={(e) => setFilter("paymentRecordId", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Date from */}
            <div>
              <Label>Date from</Label>
              <AllmarkDatePicker
                value={filters.from}
                onChange={(iso) => setFilter("from", iso)}
                placeholder="From date"
              />
            </div>

            {/* Date to */}
            <div>
              <Label>Date to</Label>
              <AllmarkDatePicker
                value={filters.to}
                onChange={(iso) => setFilter("to", iso)}
                placeholder="To date"
              />
            </div>

            {/* Amount min */}
            <div>
              <Label htmlFor="txn-min">Min amount</Label>
              <Input
                id="txn-min"
                type="number"
                placeholder="0.00"
                value={filters.minAmount}
                onChange={(e) => setFilter("minAmount", e.target.value)}
              />
            </div>

            {/* Amount max */}
            <div>
              <Label htmlFor="txn-max">Max amount</Label>
              <Input
                id="txn-max"
                type="number"
                placeholder="0.00"
                value={filters.maxAmount}
                onChange={(e) => setFilter("maxAmount", e.target.value)}
              />
            </div>
          </div>

          {/* Clear filters */}
          {Object.values(filters).some((v) => v !== "" && v !== null) && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilters(EMPTY_FILTERS); setPage(0); }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      }
    >
      {/* DataGrid wraps rows in clickable divs via onClick on row elements;
          DataGrid itself has no row-click prop, so we add onClick per-row
          via the accessor returning a clickable cell styled as an action trigger.
          Simpler: wrap each row's key column in a button-like span.
          Approach here: use DataGrid normally, and add a dedicated "action" col
          that fires navigate. Actually simplest: add an onClick wrapper using
          data-rowkey on the table row via a custom trick —
          but DataGrid doesn't expose onRowClick.
          Pattern from ReconciledTodayPage: li onClick. Here we'll add a
          "View" action column that calls navigate. */}
      <DataGrid
        rows={data?.items ?? []}
        columns={[
          ...cols,
          {
            id: "action",
            header: "",
            accessor: (r) => {
              const hasTarget = r.invoiceId != null || r.paymentRecordId != null;
              if (!hasTarget) return null;
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick(r);
                  }}
                  aria-label={
                    r.invoiceId != null
                      ? `View invoice ${r.invoiceId}`
                      : `View payment ${r.paymentRecordId}`
                  }
                >
                  View
                </Button>
              );
            },
          },
        ]}
        rowKey={(r) => r.tranId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No transactions match these filters."
        onPageChange={setPage}
      />
    </ListPage>
  );
}
